/**
 * useMetaInfoCollapseState — connection/database expansion state for the
 * Settings → Meta Info view. Defaults: collapse connections when there
 * are more than 3 connections; collapse databases when a connection has
 * more than 3 databases. User overrides win over the default.
 */

import { useState } from 'react';

export type MetaInfoCollapseState = {
  isConnectionOpen: (connectionId: string) => boolean;
  isDatabaseOpen: (
    connectionId: string,
    database: string,
    dbCount: number,
  ) => boolean;
  toggleConnection: (connectionId: string) => void;
  toggleDatabase: (
    connectionId: string,
    database: string,
    dbCount: number,
  ) => void;
};

export function useMetaInfoCollapseState(
  totalConnections: number,
): MetaInfoCollapseState {
  const [connOverride, setConnOverride] = useState<Record<string, boolean>>({});
  const [dbOverride, setDbOverride] = useState<Record<string, boolean>>({});

  const collapseConnectionsByDefault = totalConnections > 3;

  const isConnectionOpen = (connectionId: string) =>
    connectionId in connOverride
      ? connOverride[connectionId]
      : !collapseConnectionsByDefault;

  const isDatabaseOpen = (
    connectionId: string,
    database: string,
    dbCount: number,
  ) => {
    const key = `${connectionId}::${database}`;
    if (key in dbOverride) return dbOverride[key];
    return dbCount <= 3;
  };

  const toggleConnection = (connectionId: string) =>
    setConnOverride((prev) => ({
      ...prev,
      [connectionId]: !isConnectionOpen(connectionId),
    }));

  const toggleDatabase = (
    connectionId: string,
    database: string,
    dbCount: number,
  ) => {
    const key = `${connectionId}::${database}`;
    setDbOverride((prev) => ({
      ...prev,
      [key]: !isDatabaseOpen(connectionId, database, dbCount),
    }));
  };

  return {
    isConnectionOpen,
    isDatabaseOpen,
    toggleConnection,
    toggleDatabase,
  };
}
